/**
 * H1 — what build is this browser running.
 *
 * Sent as `X-App-Version` / `X-Platform` on every tRPC request (both the authed
 * client and the public booking client); the API parses them in
 * `trpc/context.factory.ts` and records them against the signed-in user.
 *
 * Web has no "app version" the way the phone does — the deployed build IS the
 * version — so this is the short git SHA of the commit the image was built
 * from, e.g. `bc07ab0`. `deploy-web.yml` passes it as a Docker `--build-arg`
 * and `Dockerfile` turns it into `NEXT_PUBLIC_BUILD_VERSION`, which is the
 * mechanism already used for `NEXT_PUBLIC_API_URL` and the two OAuth client
 * IDs. No new machinery.
 *
 * ⚠️ `NEXT_PUBLIC_*` is inlined by source-level substitution at BUILD time. It
 * must be read as a literal `process.env['NEXT_PUBLIC_BUILD_VERSION']` — a
 * computed or destructured read is not substituted and silently becomes
 * undefined in the production bundle. Same shape as `api-url.ts`, for the same
 * reason.
 *
 * The fallback is 'dev', not undefined and not empty: an un-injected build
 * (local `next dev`, a local `docker build` with no `--build-arg`) should send
 * something honest and parseable rather than nothing at all.
 */
export const APP_VERSION: string =
  typeof process !== 'undefined' ? (process.env['NEXT_PUBLIC_BUILD_VERSION'] ?? 'dev') : 'dev';

/** The API's `parseClientPlatform` allows 'ios' | 'android' | 'web'. */
export const CLIENT_PLATFORM = 'web' as const;

/** Passed as `httpBatchLink`'s `headers` option on both web tRPC clients. */
export function clientVersionHeaders(): Record<string, string> {
  return {
    'X-App-Version': APP_VERSION,
    'X-Platform': CLIENT_PLATFORM,
  };
}
